import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { useAuth } from '@/contexts/AuthContext';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { doc, getDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/lib/firebase';
import { AffiliateCode, Referral, Payout } from '@/types';
import { tierLabel, serviceLineLabel, TERMS_VERSION } from '@/lib/affiliate';
import { HowYouEarn } from '@/components/HowYouEarn';

export function AffiliatesPage() {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [xUsername, setXUsername] = useState('');
  const [xrplWallet, setXrplWallet] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [affiliateCode, setAffiliateCode] = useState<AffiliateCode | null>(null);
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  useEffect(() => {
    if (currentUser) {
      fetchAffiliateData();
    } else {
      setLoading(false);
    }
  }, [currentUser]);

  const fetchAffiliateData = async () => {
    if (!currentUser) return;

    try {
      setLoading(true);
      
      // Check if user already has an affiliate code
      const codesQuery = query(
        collection(db, 'affiliateCodes'),
        where('userId', '==', currentUser.uid)
      );
      const codesSnapshot = await getDocs(codesQuery);
      
      if (!codesSnapshot.empty) {
        const codeData = codesSnapshot.docs[0].data() as AffiliateCode;
        setAffiliateCode({ ...codeData, id: codesSnapshot.docs[0].id });
        setXUsername(codeData.xUsername);
        setXrplWallet(codeData.xrplWallet);

        // Fetch referrals for this code
        const referralsQuery = query(
          collection(db, 'referrals'),
          where('affiliateUserId', '==', currentUser.uid)
        );
        const referralsSnapshot = await getDocs(referralsQuery);
        const referralsData = referralsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Referral[];
        setReferrals(referralsData);

        // Fetch this affiliate's payout history
        const payoutsQuery = query(
          collection(db, 'payouts'),
          where('affiliateUserId', '==', currentUser.uid)
        );
        const payoutsSnapshot = await getDocs(payoutsQuery);
        const payoutsData = payoutsSnapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() })) as Payout[];
        payoutsData.sort((a, b) => (b.paidAt?.toMillis?.() || 0) - (a.paidAt?.toMillis?.() || 0));
        setPayouts(payoutsData);
      }
    } catch (err) {
      console.error('Error fetching affiliate data:', err);
      setError('Failed to load affiliate data');
    } finally {
      setLoading(false);
    }
  };

  const generateAffiliateCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    if (!currentUser) {
      setError('You must be logged in to create an affiliate code');
      return;
    }

    if (!xUsername.trim()) {
      setError('Please enter your X (Twitter) username');
      return;
    }

    if (!xrplWallet.trim()) {
      setError('Please enter your XRPL wallet address');
      return;
    }

    // Basic XRPL wallet validation (starts with 'r' and is 25-35 chars)
    if (!xrplWallet.match(/^r[1-9A-HJ-NP-Za-km-z]{24,34}$/)) {
      setError('Please enter a valid XRPL wallet address');
      return;
    }

    if (!agreedToTerms) {
      setError('Please read and agree to the Affiliate Agreement to continue');
      return;
    }

    try {
      setCreating(true);
      
      // Create the affiliate code
      const cleanUsername = xUsername.trim().replace('@', '');
      const code = `${cleanUsername}_GRID_AGENT`;

      // Check if code already exists
      const codeDoc = await getDoc(doc(db, 'affiliateCodes', code));
      if (codeDoc.exists()) {
        setError('This username is already taken. Please try a different X username.');
        return;
      }

      // Create the affiliate code document
      const affiliateData: Omit<AffiliateCode, 'id'> = {
        code,
        userId: currentUser.uid,
        userEmail: currentUser.email!,
        xUsername: cleanUsername,
        xrplWallet: xrplWallet.trim(),
        totalReferrals: 0,
        totalEarnings: 0,
        totalPaid: 0,
        createdAt: new Date() as any,
        updatedAt: new Date() as any,
      };

      // Use Cloud Function to create the affiliate code securely
      const createAffiliateCodeFn = httpsCallable(functions, 'createAffiliateCode');
      const result = await createAffiliateCodeFn({
        xUsername: cleanUsername,
        xrplWallet: xrplWallet.trim(),
        agreedToTerms: true,
        termsVersion: TERMS_VERSION,
      });

      const data = result.data as { success: boolean; code: string; message: string };
      
      if (data.success) {
        setAffiliateCode({ ...affiliateData, id: code } as AffiliateCode);
        setSuccessMessage('Your affiliate code has been created successfully!');
        
        // Refresh the data
        await fetchAffiliateData();
      }
      setSuccessMessage('Your affiliate code has been created successfully!');
      
    } catch (err: any) {
      console.error('Error creating affiliate code:', err);
      setError(err.message || 'Failed to create affiliate code');
    } finally {
      setCreating(false);
    }
  };

  const copyToClipboard = () => {
    if (affiliateCode) {
      navigator.clipboard.writeText(affiliateCode.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Personal referral link the affiliate shares with people who've agreed to
  // hear about GRID. The prospect fills it in themselves, which captures consent
  // and drops a lead straight into GRID's pipeline tagged to this affiliate.
  const referralLink = affiliateCode ? `${window.location.origin}/r/${affiliateCode.code}` : '';
  const copyReferralLink = () => {
    if (referralLink) {
      navigator.clipboard.writeText(referralLink);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    }
  };

  if (loading) {
    return <Layout><LoadingSpinner /></Layout>;
  }

  // Generate / sign-in state — wide, two-column, fits on one screen
  if (!affiliateCode) {
    return (
      <Layout wide>
        <div className="affiliate-generate">
          {/* Left — form or sign-in */}
          <div className="terminal-card" style={{ marginBottom: 0 }}>
            <div className="card-header">
              <span className="card-num">01</span>
              <span className="card-title">{!currentUser ? 'JOIN THE PROGRAM' : 'CREATE AFFILIATE CODE'}</span>
            </div>
            <div className="card-body">
              {error && <div className="terminal-alert error">Error: {error}</div>}
              {successMessage && <div className="terminal-alert success">{successMessage}</div>}

              {!currentUser ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <p className="prose prose--muted">
                    Sign in to generate your affiliate code and start earning on the referrals you bring to GRID.
                  </p>
                  <Link to="/login" className="action-btn large" style={{ textAlign: 'center', textDecoration: 'none' }}>
                    Sign In to Get Your Code
                  </Link>
                </div>
              ) : (
                <form onSubmit={generateAffiliateCode} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  <div className="field-group">
                    <label className="field-label" htmlFor="xUsername">X (TWITTER) USERNAME</label>
                    <input
                      type="text"
                      id="xUsername"
                      value={xUsername}
                      onChange={(e) => setXUsername(e.target.value)}
                      placeholder="YOUR_USERNAME"
                      className="terminal-input"
                      required
                    />
                    <span style={{ fontSize: '11px', color: 'var(--text-3)' }}>
                      Without @ symbol — your code will be: {xUsername.trim().replace('@', '') || 'USERNAME'}_GRID_AGENT
                    </span>
                  </div>

                  <div className="field-group">
                    <label className="field-label" htmlFor="xrplWallet">XRPL WALLET ADDRESS</label>
                    <input
                      type="text"
                      id="xrplWallet"
                      value={xrplWallet}
                      onChange={(e) => setXrplWallet(e.target.value)}
                      placeholder="rXXXXXXXXXXXXXXXXXXXXXXXXXXXXX"
                      className="terminal-input"
                      required
                      style={{ fontFamily: 'var(--font-mono)' }}
                    />
                    <span style={{ fontSize: '11px', color: 'var(--text-3)' }}>
                      Payouts are sent as RLUSD to this XRPL wallet — it must have an RLUSD trustline
                    </span>
                  </div>

                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={agreedToTerms}
                      onChange={(e) => setAgreedToTerms(e.target.checked)}
                      style={{ marginTop: '3px', accentColor: 'var(--accent)' }}
                    />
                    <span className="prose prose--sm prose--muted" style={{ margin: 0 }}>
                      I have read and agree to the{' '}
                      <Link to="/affiliate-terms" target="_blank" style={{ color: 'var(--accent)' }}>
                        Affiliate Agreement
                      </Link>
                      , and I confirm I will only refer people who have agreed to be contacted (no cold outreach).
                    </span>
                  </label>

                  <button
                    type="submit"
                    className="action-btn large"
                    disabled={creating || !agreedToTerms}
                    style={{ width: '100%' }}
                  >
                    {creating ? 'Creating…' : 'Generate Affiliate Code'}
                  </button>
                </form>
              )}
            </div>
          </div>

          {/* Right — how you earn */}
          <div>
            <HowYouEarn compact />
          </div>
        </div>
      </Layout>
    );
  }

  // Dashboard state — affiliate has a code
  const earned = affiliateCode.totalEarnings || 0;
  const paid = affiliateCode.totalPaid || 0;
  const pending = earned - paid;

  // Payouts are recorded in aggregate (totalPaid), not per referral. Derive a
  // per-line Paid / Partial / Pending status by allocating what's been paid
  // across earning referrals oldest-first.
  const referralStatus: Record<string, 'PAID' | 'PARTIAL' | 'PENDING'> = {};
  {
    let remaining = paid;
    const earning = [...referrals]
      .filter((r) => (r.earningsAmount || 0) > 0)
      .sort((a, b) => (a.createdAt?.toMillis?.() || 0) - (b.createdAt?.toMillis?.() || 0));
    for (const r of earning) {
      const amt = r.earningsAmount || 0;
      if (remaining >= amt) { referralStatus[r.id] = 'PAID'; remaining -= amt; }
      else if (remaining > 0) { referralStatus[r.id] = 'PARTIAL'; remaining = 0; }
      else { referralStatus[r.id] = 'PENDING'; }
    }
  }
  const statusMeta = (id: string, hasEarnings: boolean) => {
    if (!hasEarnings) return { label: '—', color: 'var(--text-3)' };
    switch (referralStatus[id]) {
      case 'PAID': return { label: 'Paid', color: 'var(--success)' };
      case 'PARTIAL': return { label: 'Partial', color: 'var(--accent)' };
      default: return { label: 'Pending', color: 'var(--text-2)' };
    }
  };

  return (
    <Layout>
      <div style={{ maxWidth: '720px', margin: '0 auto' }}>
        {/* Stats */}
        <div className="terminal-grid" style={{ marginBottom: '0', gridTemplateColumns: 'repeat(4, 1fr)' }}>
          <div className="terminal-card" style={{ marginBottom: '0' }}>
            <div className="card-header">
              <span className="card-num">01</span>
              <span className="card-title">REFERRALS</span>
            </div>
            <div className="card-body">
              <div style={{ fontSize: '28px', color: 'var(--gold)', fontWeight: 700 }}>
                {affiliateCode.totalReferrals}
              </div>
            </div>
          </div>
          <div className="terminal-card" style={{ marginBottom: '0' }}>
            <div className="card-header">
              <span className="card-num">02</span>
              <span className="card-title">EARNED</span>
            </div>
            <div className="card-body">
              <div style={{ fontSize: '28px', color: 'var(--success)', fontWeight: 700 }}>
                ${earned.toFixed(2)}
              </div>
            </div>
          </div>
          <div className="terminal-card" style={{ marginBottom: '0' }}>
            <div className="card-header">
              <span className="card-num">03</span>
              <span className="card-title">PAID</span>
            </div>
            <div className="card-body">
              <div style={{ fontSize: '28px', color: 'var(--muted)', fontWeight: 700 }}>
                ${paid.toFixed(2)}
              </div>
            </div>
          </div>
          <div className="terminal-card" style={{ marginBottom: '0' }}>
            <div className="card-header">
              <span className="card-num">04</span>
              <span className="card-title">PENDING</span>
            </div>
            <div className="card-body">
              <div style={{ fontSize: '28px', color: pending > 0 ? 'var(--gold)' : 'var(--muted)', fontWeight: 700 }}>
                ${pending.toFixed(2)}
              </div>
            </div>
          </div>
        </div>

        {/* Your code */}
        <div className="terminal-card">
          <div className="card-header">
            <span className="card-num">05</span>
            <span className="card-title">YOUR AFFILIATE CODE</span>
          </div>
          <div className="card-body">
            {successMessage && <div className="terminal-alert success">{successMessage}</div>}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {/* Code display */}
              <div style={{
                background: 'var(--surface-strong)',
                border: '1px solid var(--border-gold)',
                padding: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
              }}>
                <code style={{ fontSize: '14px', color: 'var(--gold)', fontWeight: 700, fontFamily: 'var(--font-mono)' }}>
                  {affiliateCode.code}
                </code>
                <button onClick={copyToClipboard} className="action-btn secondary" style={{ minWidth: 'auto', padding: '8px 14px' }}>
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>

              {/* Personal referral link */}
              <div className="field-group" style={{ margin: 0 }}>
                <div className="field-label">YOUR REFERRAL LINK</div>
                <div style={{
                  background: 'var(--surface-strong)',
                  border: '1px solid var(--border)',
                  padding: '12px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '12px',
                }}>
                  <code style={{ fontSize: '12px', color: 'var(--text)', fontFamily: 'var(--font-mono)', wordBreak: 'break-all' }}>
                    {referralLink}
                  </code>
                  <button onClick={copyReferralLink} className="action-btn secondary" style={{ minWidth: 'auto', padding: '8px 14px', flex: '0 0 auto' }}>
                    {linkCopied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <span style={{ fontSize: '11px', color: 'var(--text-3)' }}>
                  Share this with people who've agreed to hear about GRID. They fill in their own details, and the
                  lead comes straight to us — automatically credited to you. No cold outreach.
                </span>
              </div>

              <div className="terminal-grid">
                <div className="field-group">
                  <div className="field-label">X USERNAME</div>
                  <div style={{ fontSize: '13px', color: 'var(--text)', padding: '6px 0' }}>@{affiliateCode.xUsername}</div>
                </div>
                <div className="field-group">
                  <div className="field-label">XRPL WALLET</div>
                  <div style={{ fontSize: '11px', color: 'var(--text)', fontFamily: 'var(--font-mono)', padding: '6px 0', wordBreak: 'break-all' }}>
                    {affiliateCode.xrplWallet}
                  </div>
                </div>
              </div>

              <hr className="term-divider" />
              <div className="hero-copy" style={{ marginBottom: 0 }}>
                <span className="hero-copy--highlight">How to share your code</span>
                <span>Share with potential GRID customers — earn on every sign-up.</span>
                <span>Example: "Use code {affiliateCode.code} when signing up for GRID Support."</span>
              </div>
            </div>
          </div>
        </div>

        {/* How You Earn */}
        <HowYouEarn />

        {/* Referrals list */}
        {referrals.length > 0 && (
          <div className="terminal-card">
            <div className="card-header">
              <span className="card-num">06</span>
              <span className="card-title">YOUR REFERRALS</span>
              <div className="card-header-right">
                <span className="stat-chip">COUNT <strong>{referrals.length}</strong></span>
              </div>
            </div>
            <div className="card-body" style={{ padding: 0 }}>
              <div className="table-wrap" style={{ border: 'none' }}>
                <table className="terminal-table table-readable">
                  <thead>
                    <tr>
                      <th>REFERRED</th>
                      <th>TYPE</th>
                      <th>SERVICE</th>
                      <th>DATE</th>
                      <th>STATUS</th>
                      <th style={{ textAlign: 'right' }}>COMMISSION</th>
                    </tr>
                  </thead>
                  <tbody>
                    {referrals.map((referral) => {
                      const hasEarnings = !!referral.earningsAmount;
                      const meta = statusMeta(referral.id, hasEarnings);
                      return (
                      <tr key={referral.id}>
                        <td>{referral.referredEmail}</td>
                        <td style={{ color: 'var(--muted)' }}>
                          {referral.referralType ? tierLabel(referral.referralType) : '—'}
                        </td>
                        <td style={{ color: 'var(--muted)' }}>{serviceLineLabel(referral.serviceLine)}</td>
                        <td className="cell-tight" style={{ color: 'var(--muted)' }}>
                          {new Date(referral.createdAt.toDate()).toLocaleDateString()}
                        </td>
                        <td className="cell-tight">
                          <span style={{
                            color: meta.color,
                            fontFamily: 'var(--font-mono)',
                            fontSize: '10px',
                            fontWeight: 700,
                            letterSpacing: '0.06em',
                            textTransform: 'uppercase',
                          }}>
                            {meta.label}
                          </span>
                        </td>
                        <td className="cell-tight" style={{ textAlign: 'right', color: hasEarnings ? 'var(--success)' : 'var(--muted)', fontWeight: 700 }}>
                          {hasEarnings ? `+$${referral.earningsAmount!.toFixed(2)}` : '—'}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Payout history */}
        {affiliateCode && payouts.length > 0 && (
          <div className="terminal-card">
            <div className="card-header">
              <span className="card-num">07</span>
              <span className="card-title">PAYOUT HISTORY</span>
              <div className="card-header-right">
                <span className="stat-chip">COUNT <strong>{payouts.length}</strong></span>
              </div>
            </div>
            <div className="card-body" style={{ padding: 0 }}>
              <div className="table-wrap" style={{ border: 'none' }}>
                <table className="terminal-table table-readable">
                  <thead>
                    <tr>
                      <th>DATE</th>
                      <th>AMOUNT</th>
                      <th>TX HASH</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payouts.map((payout) => (
                      <tr key={payout.id}>
                        <td className="cell-tight" style={{ color: 'var(--muted)' }}>
                          {payout.paidAt?.toDate().toLocaleDateString()}
                        </td>
                        <td className="cell-tight" style={{ color: 'var(--success)', fontWeight: 700 }}>${(payout.amount || 0).toFixed(2)}</td>
                        <td style={{ color: 'var(--muted)', fontFamily: 'Consolas, monospace', wordBreak: 'break-all' }}>
                          {payout.txHash || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
